import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import type { AttributeDefinition, GarmentCard, SessionCommand, SlotDefinition } from '@fitting-room/contracts'
import { FilterChips } from '../../components/FilterChips'
import { LineSwitcher } from '../../components/LineSwitcher'
import { GarmentDeck } from '../../components/GarmentDeck'
import { GarmentGrid } from '../../components/GarmentGrid'
import { GownLightbox } from '../../components/GownLightbox'
import { readBrowseView, rememberBrowseView, type BrowseView } from '../../lib/browseView'
import { parseRelaxations, setAsideFilterKeys, type Relaxation } from '../../lib/relaxations'
import { uiStrings, type UiStrings } from '../../lib/strings'
import { text } from '../../lib/text'
import { useRankedGarments } from '../useSession'
import type { StepProps } from '../types'

/**
 * One relaxation, in words.
 *
 * The attribute names come from the tenant's own definitions, so a boutique
 * that calls the thing something else gets its own word — and a key this
 * client has no definition for falls back to the generic sentence rather than
 * printing a slug at the customer.
 */
export function relaxationLine(
  relaxation: Relaxation,
  t: UiStrings,
  attributes: readonly AttributeDefinition[],
  locale: string,
): string {
  switch (relaxation.kind) {
    case 'filters': {
      const names = relaxation.attributeKeys
        .map((key) => attributes.find((a) => a.key === key))
        .filter((def): def is AttributeDefinition => def !== undefined)
        .map((def) => text(def.label, locale).toLocaleLowerCase(locale))
      return names.length > 0 ? t.relaxedFilters(names.join(' + ')) : t.relaxedNotice
    }
    case 'rangeWidened':
      return t.relaxedRangeWidened
    case 'rangeDropped':
      return t.relaxedRangeDropped
    case 'orderable':
      return t.relaxedOrderable
    case 'exhausted':
      return t.relaxedExhausted
    case 'size':
      return t.relaxedSize
    case 'other':
      return t.relaxedNotice
  }
}

/** A count that is on the screen for a second and a half. See `flash`. */
interface Flash {
  readonly slotKey: string
  readonly garmentId: string
  readonly used: number
  readonly capacity: number
  /** The slot has no cap, so the pill says how many and not "of how many". */
  readonly unlimited?: boolean
  /** She pressed a slot that holds its cap and is full: nothing was sent. */
  readonly full?: boolean
}

/**
 * The history entry a gown opened from the grid stands on.
 *
 * On a phone "back" is a gesture before it is a button, and before this entry
 * existed the gesture had nothing to go back to inside the room: the URL does
 * not change from screen to screen, so it left the fitting room altogether.
 * Spread over whatever state the router keeps there, so the router reads the
 * same entry index on the way back and treats the pop as a no-op. It carries
 * the gown's place in the page, so Forward onto it opens the same gown again.
 */
const OPENED_STATE = 'frOpenedGown'

const asRecord = (state: unknown): Record<string, unknown> =>
  typeof state === 'object' && state !== null ? (state as Record<string, unknown>) : {}

/** The gown an entry was pushed for, or null for an entry that is not one of ours. */
const openedIndexOf = (state: unknown): number | null => {
  const at = asRecord(state)[OPENED_STATE]
  return typeof at === 'number' && Number.isInteger(at) && at >= 0 ? at : null
}

const openedEntryIsOnTop = (): boolean => {
  try {
    return openedIndexOf(window.history.state) !== null
  } catch {
    return false
  }
}

/**
 * The centrepiece.
 *
 * Which slots exist, what they are called, how many hooks each has, and when
 * to nudge towards converging are all server-resolved: `step.slotKeys`,
 * `flow.slots`, `slotStates[].capacity`, `overflowHintAt`. There is not a
 * single number in this file that decides what the customer is allowed to do.
 *
 * Two layouts of the same ranked page: the grid, four to a screen, which is
 * where a phone starts and where a photo tapped opens large over it; and the
 * feed, one to a screen, which is where the switch at the top goes. Same request,
 * same order, same marks with the same caps — the layout is the only thing the
 * switch changes, and it is this phone's to remember (`lib/browseView.ts`).
 */
export function BrowseStep({ step, ctx }: StepProps<'browse'>) {
  const { view, locale, token } = ctx
  const t = uiStrings(locale)

  const query = useRankedGarments(token, view)
  /**
   * The gown she is at, in either layout.
   *
   * The feed reports the one in front; the grid reports the first one still
   * on the screen. One number for both is what lets the switch keep her place:
   * whichever layout mounts next opens at it.
   */
  const [index, setIndex] = useState(0)
  /** Her layout, as she last chose it on this phone. The grid until she does. */
  const [layout, setLayout] = useState<BrowseView>(() => readBrowseView())
  /**
   * A gown she opened large from the grid, by its id — or null. By id and
   * not by its place in the page, so a page refetched under it (a stale deck
   * coming back on focus) cannot swap in a different gown.
   *
   * Not a change of layout: the grid stays mounted under it, so closing it is
   * the grid exactly where she left it. `enlargedRef` is the same flag,
   * readable synchronously: a second tap on the way out lands before the
   * re-render that would have told it the gown was already closed, and a
   * second `history.back()` is out of the room. `opener` is the photo she
   * tapped, which is where focus goes back to.
   */
  const [enlargedId, setEnlargedId] = useState<string | null>(null)
  const enlargedRef = useRef(false)
  const opener = useRef<HTMLElement | null>(null)
  /** Focus goes back to `opener` once the gown has closed. */
  const refocus = useRef(false)
  /** Bumped when the page under the grid is replaced, so the grid starts again at the top. */
  const [gridEpoch, setGridEpoch] = useState(0)
  const showGrid = layout === 'grid'
  /**
   * The count that is on the screen right now, and nothing else.
   *
   * A press puts it there and a second and a half takes it away. It is not
   * derived from `slotStates` on every render on purpose: what the pill says
   * is the answer to one press, and the fitting room is where the same numbers
   * live permanently for anyone who wants to look them up.
   */
  const [flash, setFlash] = useState<Flash | null>(null)
  /**
   * Whether the tools are open over the gowns.
   *
   * Closed is the resting state, because the screen is a feed now and a feed
   * with a toolbar on it is a search results page. What is behind the mark is
   * two things she uses in bursts — which catalog she is looking at, and
   * what narrows it — and neither is worth the top of every gown she scrolls
   * past. The mark itself says when something is in force.
   */
  const [toolsOpen, setToolsOpen] = useState(false)

  useEffect(() => {
    if (!flash) return
    const timer = window.setTimeout(() => setFlash(null), 1800)
    return () => window.clearTimeout(timer)
  }, [flash])

  const cards = useMemo(() => query.data?.items ?? [], [query.data])

  const relaxed = useMemo(() => query.data?.relaxedConstraints ?? [], [query.data])
  const relaxations = useMemo(() => parseRelaxations(relaxed), [relaxed])
  const setAside = useMemo(() => setAsideFilterKeys(relaxed), [relaxed])

  const slots = useMemo(
    () =>
      step.slotKeys
        .map((key) => view.flow.slots.find((s) => s.key === key))
        .filter((s): s is SlotDefinition => s !== undefined),
    [step.slotKeys, view.flow.slots],
  )

  const line = view.flow.lines.find((l) => l.key === view.session.lineKey) ?? view.flow.lines[0]

  /*
   * A line can turn the chips off. The rail is the case: forty pieces in her
   * size is already a short list, and every chip on it is a chance to leave
   * her looking at two gowns.
   */
  const filterable = useMemo(
    () =>
      line?.filters === false
        ? []
        : step.filterableAttributes
            .map((key) => view.tenant.attributes.find((a) => a.key === key))
            .filter((a): a is AttributeDefinition => a !== undefined && a.filterable),
    [line, step.filterableAttributes, view.tenant.attributes],
  )
  const bookingStep = view.flow.steps.find((s) => s.kind === 'booking')
  const savedCount = view.session.picks.filter((p) => step.slotKeys.includes(p.slotKey)).length

  const pickedSlotOf = (garmentId: string) => view.session.picks.find((p) => p.garmentId === garmentId)?.slotKey

  /*
   * How full the rails are is not drawn on this screen any more.
   *
   * There were two of them under the feed, and a band that slid in over them
   * at `overflowHintAt` to say "that's plenty to start with". Both are gone
   * with everything else that was under the feed: what a rail was for — where
   * am I up to — is the count that slides out of a mark on the press that
   * changes it, and what the band was for is the way on in the header, which
   * lights when she passes the same threshold. `SessionRoute` reads it.
   *
   * The next screen is her fitting room, which is where seeing all of them at
   * once belongs.
   */

  /*
   * Scrolling puts the tools away, which is the same rule the gown's own words
   * follow: a gesture that means "not this one" means "not this either". The
   * grid reports a scroll as a new first gown, so it closes them too. A filter
   * that lands also resets the gowns to the top, so choosing one closes the
   * sheet over the gown it just went and found.
   */
  useEffect(() => {
    setToolsOpen(false)
  }, [index])

  const showEnlarged = useCallback((garmentId: string) => {
    enlargedRef.current = true
    setEnlargedId(garmentId)
  }, [])

  /*
   * Closing a gown opened large.
   *
   * The screen closes it at once, whichever way she asked; then, if the entry
   * opening it pushed is still on top, it is popped, so the system's back
   * gesture and the close button leave the same history behind. Once only:
   * `enlargedRef` is down after the first tap, so a second tap — or the switch
   * pressed straight after — cannot pop a second entry, which would be the
   * page before the room. When the entry has gone (she reloaded with the gown
   * open, say) there is nothing to pop and nothing is.
   */
  const closeEnlarged = useCallback(() => {
    if (!enlargedRef.current) return
    enlargedRef.current = false
    setEnlargedId(null)
    if (openedEntryIsOnTop()) window.history.back()
    refocus.current = true
  }, [])

  /*
   * Home to the photo she tapped — after the render that closed the gown,
   * because until then the grid is `inert` and will not take focus.
   */
  useEffect(() => {
    if (enlargedId !== null || !refocus.current) return
    refocus.current = false
    opener.current?.focus({ preventScroll: true })
  }, [enlargedId])

  /** The page under her was replaced — a filter, the other catalog. Back to the top of it. */
  const restart = () => {
    closeEnlarged()
    setIndex(0)
    setGridEpoch((epoch) => epoch + 1)
  }

  /*
   * The history, read rather than kept in step by hand.
   *
   * Back off a gown's entry closes the gown, if the screen has not already.
   * Forward onto one — she came back out and changed her mind — opens that
   * gown again, without pushing a second entry over it.
   */
  /*
   * Forward reopens by the place the entry was pushed for, which is all an
   * entry can carry across a reload; `cardsRef` is the page as it is now.
   */
  const cardsRef = useRef(cards)
  cardsRef.current = cards
  const cardCount = cards.length
  useEffect(() => {
    const onPop = (event: PopStateEvent) => {
      const at = openedIndexOf(event.state)
      if (at === null) {
        if (enlargedRef.current) {
          enlargedRef.current = false
          setEnlargedId(null)
          refocus.current = true
        }
        return
      }
      const card = cardsRef.current[Math.min(at, cardCount - 1)]
      if (enlargedRef.current || !card || !showGrid) return
      showEnlarged(card.garment.id)
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [cardCount, showEnlarged, showGrid])

  /*
   * And no entry left behind.
   *
   * Arriving on the gowns with one of ours on top — a reload with a gown open,
   * the way back from her fitting room — strips the mark off it, so it is an
   * ordinary entry again rather than a gown that Back would try to close.
   * Leaving with a gown still open — Continue in the header, "Let my stylist
   * choose" — pops it, so the next Back from her fitting room is not a dead
   * press that "closes" a gown on a screen she is no longer on.
   */
  useEffect(() => {
    try {
      const state = asRecord(window.history.state)
      if (OPENED_STATE in state) {
        const { [OPENED_STATE]: _gown, ...rest } = state
        window.history.replaceState(rest, '')
      }
    } catch {
      // Nothing to strip where history cannot be read.
    }
    return () => {
      if (enlargedRef.current && openedEntryIsOnTop()) window.history.back()
    }
  }, [])

  const openFromGrid = useCallback(
    (at: number, photo: HTMLElement) => {
      const card = cardsRef.current[at]
      if (!card) return
      opener.current = photo
      showEnlarged(card.garment.id)
      try {
        window.history.pushState({ ...asRecord(window.history.state), [OPENED_STATE]: at }, '')
      } catch {
        // A browser that refuses the entry still opens the gown; the close
        // button on it is the way out.
      }
    },
    [showEnlarged],
  )

  const chooseLayout = (next: BrowseView) => {
    setToolsOpen(false)
    rememberBrowseView(next)
    closeEnlarged()
    if (next === layout) return
    setLayout(next)
  }

  /*
   * The marks, in the order they stack up the right-hand edge of the photo,
   * and the order they run left to right under a card in the grid. The primary
   * slot goes LAST, which puts it at the bottom of the feed and at the right of
   * a card: on a phone held in one hand those are the shortest reach on the
   * screen, and it is the one she presses most. Which slots exist at all is
   * `step.slotKeys`, and that is the server's answer, not this file's.
   */
  const marks = useMemo(() => {
    const primary = slots.find((s) => s.marker === 'primary')
    return [...slots.filter((s) => s.marker !== 'primary'), ...(primary ? [primary] : [])]
  }, [slots])

  /*
   * Whether the tools are holding something, which is what the mark has to say
   * while they are folded away.
   *
   * Filters only. Which catalog she is in is not a narrowing — it is a
   * question the flow asked her on its own screen and she answered, and the
   * answer is as often the second line as the first. A dot on the mark for
   * that would be lit on an ordinary session from the moment she arrives,
   * which is a warning light that means nothing.
   */
  const narrowed = Object.values(view.session.answers.filters).some((values) => values.length > 0)

  /**
   * One press of one mark, on one gown — the same in the feed and the grid.
   *
   * The count is computed here rather than read off the next response,
   * because the pill has to be on the screen before the round trip finishes —
   * and this is exactly what the server is about to say. A send that fails
   * raises the banner; the pill is gone by then.
   *
   * A slot that holds its cap refuses the pick here, in words, rather than
   * sending a pick the server will refuse with a 422 and a "that did not save"
   * banner. The button stays pressable so the refusal has somewhere to be said.
   */
  const press = (slot: SlotDefinition, garmentId: string) => {
    const isHere = pickedSlotOf(garmentId) === slot.key
    const state = view.slotStates.find((s) => s.key === slot.key)
    const full = Boolean(slot.enforce && state && state.limited !== false && !isHere && state.used >= state.capacity)
    if (state) {
      setFlash({
        slotKey: slot.key,
        garmentId,
        used: full ? state.used : Math.max(0, state.used + (isHere ? -1 : 1)),
        capacity: state.capacity,
        ...(state.limited === false ? { unlimited: true } : {}),
        ...(full ? { full: true } : {}),
      })
    }
    if (full) return
    ctx.send([
      isHere
        ? { type: 'unpick', garmentId }
        : { type: 'pick', garmentId, slotKey: slot.key, addedBy: 'customer' },
    ])
  }

  /** What the pill says: "Must try 3/5", "Favorite 4", or that the list is full. */
  const countWords = (counted: Flash, label: string): ReactNode =>
    counted.full ? (
      <span>{t.slotFull(label)}</span>
    ) : (
      <>
        <span>{label}</span>
        <span className="tabular-nums">
          {counted.unlimited ? String(counted.used) : `${counted.used}/${counted.capacity}`}
        </span>
      </>
    )

  /*
   * `press` closes over the session, which changes on every pick; the marks
   * reach it through a ref so the grid's `actions` changes only when what the
   * marks SHOW does — and the grid's memoized cards are not all drawn again
   * every time she scrolls a row.
   */
  const pressRef = useRef(press)
  pressRef.current = press
  const picks = view.session.picks
  const pending = ctx.pending

  const markButton = useCallback(
    (slot: SlotDefinition, card: GarmentCard, size: 'feed' | 'grid', describedBy?: string) => {
      const isHere = picks.find((p) => p.garmentId === card.garment.id)?.slotKey === slot.key
      return (
        <button
          key={slot.key}
          type="button"
          className={size === 'grid' ? 'fr-feed-mark fr-grid-mark' : 'fr-feed-mark'}
          data-marker={slot.marker}
          data-selected={isHere}
          aria-pressed={isHere}
          aria-label={text(slot.label, locale)}
          aria-describedby={describedBy}
          disabled={pending}
          onClick={() => pressRef.current(slot, card.garment.id)}
        >
          <SlotGlyph marker={slot.marker} filled={isHere} size={size === 'grid' ? 20 : 23} />
        </button>
      )
    },
    [picks, pending, locale],
  )

  const gridActions = useCallback(
    (card: GarmentCard, describedBy: string) => marks.map((slot) => markButton(slot, card, 'grid', describedBy)),
    [marks, markButton],
  )

  /*
   * The pill in the grid.
   *
   * In the feed it slides out to the left of the mark that was pressed. A grid
   * card is 170px wide and "Must try is full — take one off to add another"
   * is not, so here it is one pill for the screen, over the foot of the grid
   * and deaf to the pointer, saying the same words.
   */
  const flashSlot = flash ? marks.find((s) => s.key === flash.slotKey) : undefined
  const gridFlash = showGrid ? flashSlot : undefined
  const enlarged = showGrid && enlargedId !== null ? cards.find((c) => c.garment.id === enlargedId) : undefined
  /*
   * The page moved out from under an open gown — it is not on it any more.
   * Close it properly, history entry and all, rather than let the dialog
   * vanish and leave a Back press that closes nothing.
   */
  const enlargedGone = enlargedId !== null && !enlarged && !query.isFetching
  useEffect(() => {
    if (enlargedGone) closeEnlarged()
  }, [enlargedGone, closeEnlarged])
  /** The count a press just changed, for the grid's foot or the open gown. */
  const gridCount =
    gridFlash && flash ? (
      <span className="fr-feed-count fr-grid-toast" data-marker={gridFlash.marker} aria-hidden="true">
        {countWords(flash, text(gridFlash.label, locale))}
      </span>
    ) : null
  /*
   * What a screen reader hears, from a live region that is always there.
   *
   * The pills are drawn and taken away with the press, and a `role="status"`
   * that arrives already holding its words is one most screen readers never
   * announce — the region has to exist before the text changes inside it. So
   * the pills are pictures (`aria-hidden`) and this is where the count is said,
   * in both layouts.
   */
  const announcement = (() => {
    if (!flash || !flashSlot) return ''
    const label = text(flashSlot.label, locale)
    if (flash.full) return t.slotFull(label)
    return `${label} ${flash.unlimited ? flash.used : `${flash.used}/${flash.capacity}`}`
  })()

  /*
   * No heading here. `RoomHeader` is drawing it — see `SessionRoute` — which
   * is 60px of gowns bought with a line of chrome that was already on the
   * screen. It stays the page's one `h1`; the swap is where it is drawn, not
   * whether it exists.
   */
  return (
    /*
      One screen, exactly.
      What used to be under the feed — the counter, the nudge, the two rails,
      the Continue — is gone, and with it the second scrollbar: the page had
      one and the feed had another, and a thumb on a photo could not tell
      which it was about to move. The way on is in the header now, where the
      screen has a line to spare.
    */
    <div className="mx-auto flex min-h-0 w-full max-w-[520px] flex-1 flex-col px-5 pt-2">
      {/*
        The bar over the gowns: what narrows them on the left, how they are
        laid out on the right. Not `sticky` — it does not need to be. The room
        is the viewport on this screen and the gowns scroll inside their own
        box under it, so the bar is where it is for as long as she is here.
      */}
      {/* Out of reach behind an open gown, for a screen reader as for a thumb. */}
      <div className="fr-browse-bar" inert={enlarged ? true : undefined}>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="fr-iconbtn fr-browse-tool"
            data-active={narrowed ? true : undefined}
            aria-pressed={toolsOpen}
            aria-label={t.filters}
            onClick={() => setToolsOpen((open) => !open)}
          >
            {/* Three rules, shortest last: the mark every phone already
                means "narrow this down" by. */}
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
              <path d="M4 7h16M7 12h10M10 17h4" />
            </svg>
          </button>
        </div>
        {/*
          Two buttons with a pressed state rather than a toggle, because each
          one names where it goes and a screen reader says which is down.
        */}
        <div className="fr-browse-switch" role="group" aria-label={t.browseViews}>
          <button
            type="button"
            aria-pressed={showGrid}
            aria-label={t.gridView}
            title={t.gridView}
            onClick={() => chooseLayout('grid')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
              <rect x="4" y="4" width="6.5" height="7.5" rx="0.5" />
              <rect x="13.5" y="4" width="6.5" height="7.5" rx="0.5" />
              <rect x="4" y="13.5" width="6.5" height="7.5" rx="0.5" />
              <rect x="13.5" y="13.5" width="6.5" height="7.5" rx="0.5" />
            </svg>
          </button>
          <button
            type="button"
            aria-pressed={!showGrid}
            aria-label={t.singleView}
            title={t.singleView}
            onClick={() => chooseLayout('single')}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true">
              <rect x="6" y="3.5" width="12" height="17" rx="0.5" />
            </svg>
          </button>
        </div>
      </div>

      <div className="relative flex min-h-0 flex-1 flex-col">
        {query.isPending ? (
          <div className="flex flex-1 items-center justify-center">
            <span className="fr-eyebrow animate-pulse">{t.loading}</span>
          </div>
        ) : cards.length === 0 ? (
          <div className="flex flex-1 items-center justify-center px-8 text-center">
            <p className="text-sm" style={{ color: 'var(--fr-ink-dim)' }}>
              {t.emptyDeck}
            </p>
          </div>
        ) : showGrid ? (
          <GarmentGrid
            key={gridEpoch}
            cards={cards}
            locale={locale}
            index={Math.min(index, cards.length - 1)}
            inert={Boolean(enlarged)}
            onScrolled={setIndex}
            onOpen={openFromGrid}
            actions={gridActions}
          />
        ) : (
          <GarmentDeck
            cards={cards}
            attributes={view.tenant.attributes}
            locale={locale}
            index={Math.min(index, cards.length - 1)}
            onIndexChange={setIndex}
            onFactsOpen={() => setToolsOpen(false)}
            sheetOpen={toolsOpen}
            actions={(card) =>
              marks.map((slot) => {
                const label = text(slot.label, locale)
                /*
                 * The count belongs to the gown AND to the slot she just
                 * pressed, so a pill left over from the gown above cannot
                 * appear on this one — scrolling past a mark is not a press.
                 */
                const counted = flash && flash.slotKey === slot.key && flash.garmentId === card.garment.id ? flash : null
                return (
                  <div key={slot.key} className="fr-feed-action">
                    {counted ? (
                      /*
                       * The only number printed over a photo, and only for as
                       * long as it takes to read. The words are said by the
                       * live region below rather than by this, because the
                       * press it answers is a press whose button NAME cannot
                       * change — "Must try" has to hold still under a screen
                       * reader.
                       *
                       * Never clamped: past the cap it reads 5/4. Browse does
                       * not enforce capacity (C9) — collect first, converge in
                       * the shortlist rounds — so this is information about
                       * where she is, not a gate she has hit.
                       */
                      <span className="fr-feed-count" data-marker={slot.marker} aria-hidden="true">
                        {countWords(counted, label)}
                      </span>
                    ) : null}
                    {markButton(slot, card, 'feed')}
                  </div>
                )
              })
            }
          />
        )}

        {enlarged ? null : gridCount}

        {enlarged ? (
          <GownLightbox
            key={enlarged.garment.id}
            card={enlarged}
            locale={locale}
            marks={(describedBy) => marks.map((slot) => markButton(slot, enlarged, 'grid', describedBy))}
            count={flash?.garmentId === enlarged.garment.id ? gridCount : null}
            onClose={closeEnlarged}
          />
        ) : null}

        <span className="sr-only" role="status" aria-live="polite">
          {announcement}
        </span>

        {toolsOpen ? (
          <div className="fr-feed-sheet">
            {/* What this line is, in the boutique's words, where she went to
                find out which gowns she is looking at. */}
            {line?.intro ? (
              <p className="text-[0.875rem] leading-relaxed" style={{ color: 'var(--fr-ink-dim)' }}>
                {text(line.intro, locale)}
              </p>
            ) : null}
            {/* Which catalog, before which filters — the question "whose gowns
                am I looking at" comes before "narrow them down". */}
            <LineSwitcher
              lines={view.flow.lines}
              activeKey={view.session.lineKey}
              locale={locale}
              disabled={ctx.pending}
              onChange={(command) => {
                restart()
                ctx.send([command])
              }}
            />
            <FilterChips
              attributes={filterable}
              active={view.session.answers.filters}
              setAside={setAside}
              locale={locale}
              disabled={ctx.pending}
              onChange={(command: SessionCommand) => {
                restart()
                ctx.send([command])
              }}
            />
            {/*
              What the ranking had to set aside, said one line per thing
              rather than as a single sentence about "widening the search" —
              which is true of the budget and false of a filter that was
              dropped outright.

              It is in here, with the chips, because that is what it is about:
              a chip she set that the ranking could not keep is drawn dashed
              two rows above this sentence, and the sentence is what says why.
              Nothing is printed over the gown.
            */}
            {relaxations.length > 0 ? (
              <div className="flex flex-col gap-1" role="status">
                {relaxations.map((relaxation) => (
                  <p key={relaxation.kind} className="text-[0.8125rem]" style={{ color: 'var(--fr-ink-dim)' }}>
                    {relaxationLine(relaxation, t, filterable, locale)}
                  </p>
                ))}
              </div>
            ) : null}
            {/*
              The way past the gowns for someone who would rather be dressed
              than choose. Only while she has chosen nothing: a customer with
              three must-tries is not asking her stylist to start from scratch.
            */}
            {step.stylistChoosesLabel && savedCount === 0 ? (
              <button
                type="button"
                className="fr-btn-quiet fr-btn-target self-start"
                style={{ paddingInline: 0 }}
                disabled={ctx.pending}
                onClick={() => (bookingStep ? ctx.goTo(bookingStep.id) : ctx.advance())}
              >
                {text(step.stylistChoosesLabel, locale)}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  )
}

/**
 * The mark inside a pick button: a star for the primary slot, a heart for the
 * others.
 *
 * Two shapes the client is allowed to know, because neither of them is a thing
 * this boutique sells — they name what the PRESS means, the way a checkmark
 * does, and a shop selling spectacles would want the same two. The word is
 * still the button's accessible name ("Must try"), which is the tenant's own;
 * this is what a thumb aims at on a photo, where a word would need a box and a
 * box would need the room the gown is using.
 *
 * Filled when chosen and outlined when not, so the state is a shape and not
 * only a color.
 */
function SlotGlyph({
  marker,
  filled,
  size = 23,
}: {
  readonly marker: 'primary' | 'secondary'
  readonly filled: boolean
  readonly size?: number
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {marker === 'primary' ? (
        <path d="m12 4.2 2.4 4.9 5.4.8-3.9 3.8.9 5.4-4.8-2.6-4.8 2.6.9-5.4-3.9-3.8 5.4-.8Z" />
      ) : (
        <path d="M12 19.5 4.8 12.6a4.3 4.3 0 0 1 6.1-6l1.1 1.1 1.1-1.1a4.3 4.3 0 0 1 6.1 6Z" />
      )}
    </svg>
  )
}
